// Configuration des types d'activités faites par un élève.
// Les couleurs correspondent à la palette Mantine déjà utilisée dans le projet.

export const ACTIVITY_TYPES = {
  DIAGNOSTIC_MATHS: {
    value: "DIAGNOSTIC_MATHS",
    label: "Diagnostic Maths",
    color: "blue",
  },
  DIAGNOSTIC_LECTURE: {
    value: "DIAGNOSTIC_LECTURE",
    label: "Diagnostic Lecture",
    color: "grape",
  },
  LESSON_QUIZ: {
    value: "LESSON_QUIZ",
    label: "Quiz de leçon",
    color: "teal",
  },
  EXERCISE_CORRECTION: {
    value: "EXERCISE_CORRECTION",
    label: "Correction d'exercice",
    color: "orange",
  },
};

// Options prêtes à l'emploi pour un composant Select.
export const ACTIVITY_TYPE_OPTIONS = Object.values(ACTIVITY_TYPES).map(
  ({ value, label }) => ({ value, label }),
);

export const getActivityType = (type) =>
  ACTIVITY_TYPES[type] ?? { value: type, label: type, color: "gray" };

// Un diagnostic (maths / lecture) affiche des questions type "feuille de devoir".
export const isDiagnostic = (type) =>
  type === "DIAGNOSTIC_MATHS" || type === "DIAGNOSTIC_LECTURE";

// Certaines questions de maths sont encodées en tokens JSON :
//   {"type":"n","value":"52987"} {"type":"s","value":"+"} {"type":"n","value":"27645"} ...
// On les transforme en une expression lisible : "52987 + 27645 = 80632".
const OPERATOR_LABELS = { "*": "×", "/": "÷" };

export const parseMathExpression = (raw) => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{")) return null;
  const matches = trimmed.match(/\{[^}]*\}/g);
  if (!matches) return null;
  try {
    return matches
      .map((token) => {
        const obj = JSON.parse(token);
        const value = obj?.value ?? "";
        return OPERATOR_LABELS[value] ?? value;
      })
      .join(" ")
      .trim();
  } catch {
    return null;
  }
};

// Détecte un contenu HTML (issu de l'éditeur Quill) vs du texte brut.
export const looksLikeHtml = (value) =>
  typeof value === "string" && /<[a-z][\s\S]*>/i.test(value);

// Normalise le champ `choices` : peut être null, une chaîne, ou un tableau.
// Renvoie un tableau de propositions non vides et dédupliquées.
export const normalizeChoices = (choices) => {
  if (!choices) return [];
  const list = Array.isArray(choices) ? choices : [choices];
  return [...new Set(list.map((c) => (c ?? "").toString().trim()))].filter(
    Boolean,
  );
};

export const parseNumber = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value.toString().replace(",", ".").trim());
  return Number.isFinite(n) ? n : null;
};

const decimalPlaces = (value) => {
  const str = value.toString().trim();
  const idx = str.indexOf(".");
  return idx === -1 ? 0 : str.length - idx - 1;
};

const roundTo = (num, decimals) => {
  const factor = Math.pow(10, decimals);
  return Math.round(num * factor) / factor;
};

const truncTo = (num, decimals) => {
  const factor = Math.pow(10, decimals);
  return Math.trunc(num * factor) / factor;
};

// Trouve l'indice de la proposition correspondant à une valeur. Les propositions
// sont souvent arrondies OU tronquées à 2 décimales (ex: "1690.29" et "93.29"
// correspondent à "1690.2916..." et "93.29729..."). On accepte donc l'arrondi
// et la troncature, avec un repli sur la proposition numériquement la plus proche.
export const findMatchingChoiceIndex = (choices, value) => {
  if (!choices?.length || value === null || value === undefined) return -1;

  const target = value.toString().trim();
  const exact = choices.findIndex((c) => c.toString().trim() === target);
  if (exact !== -1) return exact;

  const targetNum = parseNumber(value);
  if (targetNum === null) return -1;

  // 1) Arrondi ou troncature à la précision de la proposition.
  let best = -1;
  let bestDiff = Infinity;
  choices.forEach((choice, i) => {
    const n = parseNumber(choice);
    if (n === null) return;
    const decimals = decimalPlaces(choice);
    if (n === roundTo(targetNum, decimals) || n === truncTo(targetNum, decimals)) {
      const diff = Math.abs(n - targetNum);
      if (diff < bestDiff) {
        best = i;
        bestDiff = diff;
      }
    }
  });
  if (best !== -1) return best;

  // 2) Repli : proposition la plus proche, si l'écart reste faible
  // (≤ 1% de la valeur) pour éviter un faux positif.
  choices.forEach((choice, i) => {
    const n = parseNumber(choice);
    if (n === null) return;
    const diff = Math.abs(n - targetNum);
    if (diff < bestDiff) {
      best = i;
      bestDiff = diff;
    }
  });
  const tolerance = Math.max(Math.abs(targetNum) * 0.01, 0.01);
  return bestDiff <= tolerance ? best : -1;
};
